import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import DocumentPlusIcon from '@heroicons/react/24/outline/DocumentPlusIcon';
import PaperClipIcon from '@heroicons/react/24/outline/PaperClipIcon';
import { useRef, useState } from 'react';
import type { PartnerProposalFile } from '@/components/Applications/EnterpriseLBAForm/type';
import Confirm from '@/components/UI/Modal/Confirm';

export interface AttachmentUploadConfig {
  onUpload: (files: File[]) => void;
  isUploading?: boolean;
}
interface AttachmentContainerProps {
  title: string;
  children?: React.ReactNode;
  isEmpty?: boolean;
  uploadConfig?: AttachmentUploadConfig;
  accept?: string;
}
const AttachmentContainer = (props: AttachmentContainerProps) => {
  const {
    title,
    children,
    isEmpty,
    accept,
    uploadConfig = {
      onUpload: () => {},
      isUploading: false,
    },
  } = props;
  const { isUploading, onUpload } = uploadConfig;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetFileInput = () => {
    fileInputRef.current!.value = '';
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files?.length) {
      onUpload(Array.from(files));
      resetFileInput();
    } else {
      resetFileInput();
    }
  };

  return (
    <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
      <div className='sm:col-span-2'>
        <dt className='text-sm font-medium text-gray-500 mb-2'>{title}</dt>
        <dd className='mt-1 text-sm text-gray-900'>
          <ul role='list' className='border border-gray-200 rounded-md divide-y divide-gray-200'>
            {uploadConfig && (
              <li
                className={`pl-3 pr-4 py-3 flex items-center justify-between text-sm ${isUploading ? 'cursor-wait' : 'cursor-pointer'}`}
                role='button'
                onClick={() => {
                  if (!isUploading) {
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  className='hidden'
                  name='additionalDocument'
                  type='file'
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept={accept}
                />
                <div className='flex justify-center w-full '>
                  <DocumentPlusIcon
                    className={`shrink-0 h-5 w-5 text-gray-400 ${isUploading ? '' : 'text-primaryColor'} mr-2`}
                    aria-hidden='true'
                  />
                  <p className={`text-center ${isUploading ? '' : 'text-primaryColor font-bold'}`}>
                    {isUploading ? 'Uploading...' : `Upload ${title}`}
                  </p>
                </div>
              </li>
            )}
            {children}
            {isEmpty && (
              <li className='pl-3 pr-4 py-3 flex items-center justify-between text-sm'>
                <p className='w-full text-center'>No {title}</p>
              </li>
            )}
          </ul>
        </dd>
      </div>
    </dl>
  );
};

interface AttachmentProps {
  title: string;
  doc: PartnerProposalFile | undefined;
  onClick: () => void;
  deleteFileHandler: (key: string) => void;
}
export const Attachment = (props: AttachmentProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { title, doc, onClick, deleteFileHandler } = props;

  const deleteItemHandler = () => {
    setShowDeleteConfirm(true);
  };

  const deleteItemConfirmHandler = () => {
    if (doc) {
      deleteFileHandler(doc.key);
      setShowDeleteConfirm(false);
    }
  };

  const deleteItemCancelHandler = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <li className='pl-3 pr-4 py-3 flex items-center justify-between text-sm'>
      {showDeleteConfirm && (
        <Confirm
          title='Delete File'
          message={`You are about to delete ${doc?.name} file. Are you sure you want to delete ?`}
          acceptButtonLabel='Delete'
          declineButtonLabel='Cancel'
          accept={deleteItemConfirmHandler}
          decline={deleteItemCancelHandler}
        />
      )}
      <div className='w-0 flex-1 flex items-center'>
        <PaperClipIcon className='shrink-0 h-5 w-5 text-gray-400' aria-hidden='true' />
        <span className='ml-2 flex-1 w-0 truncate'>{title}</span>
      </div>
      <div className='ml-4 shrink-0 flex gap-4 items-center'>
        <a
          href={doc?.url}
          target='_blank'
          rel='noreferrer'
          download
          className='font-medium text-blue-600 hover:text-blue-500 bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-2 cursor-pointer transition duration-300 ease-in-out'
        >
          <ArrowDownTrayIcon className='h-4 w-4' />
        </a>

        <span
          className='bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-2 cursor-pointer transition duration-300 ease-in-out'
          onClick={deleteItemHandler}
        >
          <TrashIcon className='h-4 w-4 text-tertiaryColor-normal' />
        </span>
      </div>
    </li>
  );
};

export default AttachmentContainer;
